import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { 
  FileText, 
  Brain, 
  Search, 
  PenTool, 
  CheckCircle, 
  XCircle, 
  Clock,
  Loader2
} from 'lucide-react';
import { Card } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import { AgentFlowStep } from '../types';
import DocumentProcessingResults from './DocumentProcessingResults';
import InitialAssessmentResults from './InitialAssessmentResults';
import ResponseRefinementResults from './ResponseRefinementResults';
import QuestionMatrix from './QuestionMatrix';
import PerformanceAnalysisResults from './PerformanceAnalysisResults';
import FinalReportResults from './FinalReportResults';
import AnalysisStatusCard from './AnalysisStatusCard';
import { refineResponse } from '../services/refinementService';
import { transformRefinedQuestions, hasRefinedQuestionsData, buildFinalReport, isGenerateMode } from '../utils/dataTransformers';
import { AnalysisAPI } from '../services/api';

const FlowContainer = styled.div`
  width: 100%;
  margin: 0;
`;

const TabsContainer = styled.div`
  border-bottom: 1px solid ${theme.colors.border};
  margin-bottom: ${theme.spacing[6]};
`;

const TabsList = styled.div`
  display: flex;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const TabItem = styled.button<{ $active: boolean; $completed: boolean; $disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  border: none;
  background: none;
  color: ${({ $active, $completed }) => 
    $active ? theme.colors.primary[700] : 
    $completed ? theme.colors.success[600] : 
    theme.colors.textSecondary
  };
  font-weight: ${({ $active }) => $active ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.medium};
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ $disabled }) => $disabled ? 0.5 : 1};
  white-space: nowrap;
  border-bottom: 2px solid ${({ $active }) => 
    $active ? theme.colors.primary[500] : 'transparent'
  };
  transition: all ${theme.transitions.base};

  &:hover:not(:disabled) {
    color: ${theme.colors.primary[600]};
    background: ${theme.colors.primary[100]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const TabIcon = styled.div<{ $status: 'pending' | 'in_progress' | 'completed' | 'error' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: white;
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.bold};

  ${({ $status }) => {
    switch ($status) {
      case 'completed':
        return `
          background: ${theme.colors.success[500]};
          color: white;
        `;
      case 'in_progress':
        return `
          background: ${theme.colors.primary[500]};
          color: white;
        `;
      case 'error':
        return `
          background: ${theme.colors.error[500]};
          color: white;
        `;
      default:
        return `
          background: ${theme.colors.primary[100]};
          color: ${theme.colors.textMuted};
        `;
    }
  }}
`;

const TabContent = styled.div`
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StepsContainer = styled.div`
  position: relative;
  min-height: 400px;
`;

const StepCard = styled(Card)<{ $status: 'pending' | 'in_progress' | 'completed' | 'error' }>`
  position: relative;
  overflow: hidden;
  transition: all ${theme.transitions.base};
  width: 100%;
  margin-bottom: ${theme.spacing[6]};
  
  ${({ $status }) => {
    switch ($status) {
      case 'completed':
        return `
          border-color: ${theme.colors.success[500]};
          background: ${theme.colors.success[100]};
        `;
      case 'in_progress':
        return `
          border-color: ${theme.colors.primary[500]};
          background: ${theme.colors.primary[100]};
        `;
      case 'error':
        return `
          border-color: ${theme.colors.error[500]};
          background: ${theme.colors.error[100]};
        `;
      default:
        return `
          border-color: ${theme.colors.border};
        `;
    }
  }}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${({ $status }) => {
      switch ($status) {
        case 'completed':
          return theme.colors.success[500];
        case 'in_progress':
          return theme.colors.primary[500];
        case 'error':
          return theme.colors.error[500];
        default:
          return theme.colors.border;
      }
    }};
  }
`;

const StepHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const StepIcon = styled.div<{ $status: 'pending' | 'in_progress' | 'completed' | 'error' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  flex-shrink: 0;
  
  ${({ $status }) => {
    switch ($status) {
      case 'completed':
        return `
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
        `;
      case 'in_progress':
        return `
          background: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[700]};
        `;
      case 'error':
        return `
          background: ${theme.colors.error[100]};
          color: ${theme.colors.error[700]};
        `;
      default:
        return `
          background: ${theme.colors.backgroundSecondary};
          color: ${theme.colors.textMuted};
        `;
    }
  }}
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  margin: 0 0 ${theme.spacing[2]} 0;
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.lg};
`;

const StepDescription = styled.p`
  margin: 0 0 ${theme.spacing[3]} 0;
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  line-height: 1.5;
`;

const StepStatus = styled.div<{ $status: 'pending' | 'in_progress' | 'completed' | 'error' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  
  color: ${({ $status }) => {
    switch ($status) {
      case 'completed':
        return theme.colors.success[700];
      case 'in_progress':
        return theme.colors.primary[700];
      case 'error':
        return theme.colors.error[700];
      default:
        return theme.colors.textMuted;
    }
  }};
`;

const StepResult = styled.div`
  margin-top: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily.mono};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  max-height: 400px;
  overflow-y: auto;
`;

const ErrorMessage = styled.div`
  margin-top: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.error[100]};
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.error[700]};
  font-size: ${theme.typography.fontSize.sm};
`;


const NoAnalysisState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
  color: ${theme.colors.textSecondary};
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.borderRadius.lg};
  border: 2px dashed ${theme.colors.border};
`;

const NoAnalysisText = styled.p`
  margin: ${theme.spacing[4]} 0 0 0;
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.textSecondary};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${theme.colors.backgroundSecondary};
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: ${theme.spacing[6]};
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, ${theme.colors.primary[500]}, ${theme.colors.primary[600]});
  border-radius: 2px;
  transition: width 0.3s ease;
  width: ${({ $progress }) => $progress}%;
`;

interface AgentFlowProps {
  steps: AgentFlowStep[];
  isLoading: boolean;
  threadId?: string | null;
  isCompletedAnalysis?: boolean; // New prop to indicate this is a completed analysis view
  progress?: {
    currentStep: string;
    progress: number;
    message: string;
    isComplete: boolean;
    hasError: boolean;
    error?: string;
  };
  analysisData?: {
    questions?: any[];
    evaluation_criteria?: any[];
    requirements_summary?: string;
    initial_review_result?: any;
    initial_score_report?: any;
    score_report?: string;
    refined_questions?: any[];
    refined_responses?: any[];
    refinement_path_used?: 'generic_best_practices' | 'tender_bank_patterns' | null;
    // P1 — auto-drafted answers for questions with no inline content.
    drafted_questions?: any[];
    // P2 — per-question scoring breakdown for the explainability drawer.
    question_selection_metadata?: Record<string, any>;
    // P6 — procedural / declarative items the bidder still has to address.
    compliance_items?: any[];
    // Tender bank similarity — keyed by question_id.
    past_tender_matches?: Record<string, any[]>;
    performance_report?: any;
    quality_check_result?: any;
    improvement_analysis?: any;
    comparison_content?: string;
    comparison_path?: string;
    final_report?: any;
    // LangExtract provenance fields
    extraction_method_used?: 'llm' | 'langextract';
    extraction_visualization_html?: string;
    extraction_provenance?: Record<string, any>;
    detected_tender_type?: 'rfp' | 'itt' | 'rfq' | null;
  } | null;
}

const defaultSteps: AgentFlowStep[] = [
  {
    id: 'process_pdf',
    name: 'Document Processing',
    status: 'pending',
    description: 'Extracting and processing the tender document content using OCR and text analysis.',
  },
  {
    id: 'initial_assessment',
    name: 'Initial Assessment',
    status: 'pending',
    description: 'AI reviewer analyzes responses and generates initial scoring report.',
  },
  {
    id: 'response_refinement',
    name: 'Response Refinement',
    status: 'pending',
    description: 'Refining each response and scoring the improvement against the tender\'s evaluation criteria.',
  },
  {
    id: 'performance_analysis',
    name: 'Performance Analysis',
    status: 'pending',
    description: 'Analyzing potential ROI and competitive positioning of refined responses.',
  },
  {
    id: 'final_report',
    name: 'Final Report',
    status: 'pending',
    description: 'Generating comprehensive analysis report and deliverable documents.',
  },
];

const getAnalysisStatus = (
  isLoading: boolean, 
  progress?: { currentStep: string; progress: number; message: string; isComplete: boolean; hasError: boolean; error?: string },
  analysisData?: any,
  isCompletedAnalysis?: boolean
): 'ready' | 'starting' | 'running' | 'completed' | 'failed' | 'paused' => {
  // If this is a completed analysis view, always return completed if we have data
  if (isCompletedAnalysis && analysisData) {
    return 'completed';
  }
  
  if (progress?.hasError) return 'failed';
  
  // Only mark as completed when ALL phases are done: document processing, initial assessment, response refinement, performance analysis and final report
  if (progress?.isComplete && analysisData) {
    const hasDocumentProcessing = analysisData?.questions && analysisData?.evaluation_criteria && analysisData?.requirements_summary;
    const hasInitialAssessment = analysisData?.initial_review_result;
    const hasResponseRefinement = analysisData?.refined_questions && analysisData.refined_questions.length > 0;
    const hasPerformanceAnalysis = analysisData?.improvement_analysis || analysisData?.comparison_content;
    const hasFinalReport = analysisData?.final_report;
    
    if (hasDocumentProcessing && hasInitialAssessment && hasResponseRefinement && hasPerformanceAnalysis && hasFinalReport) {
      return 'completed';
    }
  }
  
  const progressValue = progress?.progress ?? 0;
  if (isLoading || progressValue > 0) {
    if (progress?.currentStep === 'starting' || progressValue < 10) return 'starting';
    return 'running';
  }
  if (analysisData && Object.keys(analysisData).length > 0) return 'running';
  return 'ready';
};

const AgentFlow: React.FC<AgentFlowProps> = ({ steps, isLoading, threadId, isCompletedAnalysis = false, progress, analysisData }) => {
  const [displaySteps, setDisplaySteps] = useState<AgentFlowStep[]>(defaultSteps);
  const [activeTabIndex, setActiveTabIndex] = useState(0);


  useEffect(() => {
    if (steps.length > 0) {
      setDisplaySteps(steps);
    }
  }, [steps]);

  useEffect(() => {
    // When the run is finished (hydrated view or progress.isComplete fired), a
    // step is never "in_progress" — it's either "completed" (data present) or
    // legitimately absent (e.g., initial_review skipped in generate mode, or a
    // tender with zero formal evaluation criteria). Without this guard, a
    // completed run with questions:[21] / evaluation_criteria:[] gets stuck
    // showing "Document Processing — In Progress" under the "Response
    // Generation Complete!" banner.
    const runIsDone = isCompletedAnalysis || progress?.isComplete;
    // Update step statuses based on data availability and loading state
    setDisplaySteps(prevSteps => {
      const updatedSteps = [...prevSteps];
      
      // Update first step (Document Processing)
      const firstStep = updatedSteps[0];
      let firstStepStatus: 'pending' | 'in_progress' | 'completed' | 'error' = firstStep?.status || 'pending';
      
      if (firstStep && firstStep.id === 'process_pdf') {
        let newStatus: 'pending' | 'in_progress' | 'completed' = 'pending';

        // Check if we have document processing data
        const hasQuestions = analysisData?.questions && analysisData.questions.length > 0;
        const hasCriteria = analysisData?.evaluation_criteria && analysisData.evaluation_criteria.length > 0;
        const hasSummary = analysisData?.requirements_summary;
        // Any signal from a downstream node means process_tender_pdf has
        // already finished — the run wouldn't be classifying / drafting /
        // refining without extracted questions to work on. We use this
        // forward-looking signal to flip step 1 green even when the
        // extractor produced an unconventional payload (zero criteria, zero
        // summary on heavily-redacted blank ITTs).
        const hasDownstreamSignal = !!(
          analysisData?.initial_review_result ||
          (analysisData?.refined_questions && (analysisData.refined_questions as any[]).length > 0) ||
          (analysisData?.drafted_questions && (analysisData.drafted_questions as any[]).length > 0) ||
          analysisData?.quality_check_result ||
          analysisData?.improvement_analysis
        );

        // Mark completed when:
        //   • all three classic pieces are present (the happy path), OR
        //   • the run is finished AND we have any data, OR
        //   • a downstream node has emitted output (proves step 1 is done).
        if (hasQuestions && hasCriteria && hasSummary) {
          newStatus = 'completed';
        } else if (hasQuestions && hasSummary) {
          // Most tenders carry a summary even when criteria are absent —
          // accept this as "done" since the next pipeline stages don't gate
          // on criteria existence.
          newStatus = 'completed';
        } else if (runIsDone && (hasQuestions || hasCriteria || hasSummary)) {
          newStatus = 'completed';
        } else if (hasDownstreamSignal) {
          newStatus = 'completed';
        }
        // Mark as in_progress if we have any data or are loading — but never
        // while the run is finished.
        else if (!runIsDone && (isLoading || hasQuestions || hasCriteria || hasSummary)) {
          newStatus = 'in_progress';
        }

        firstStepStatus = newStatus;
        
        updatedSteps[0] = {
          ...firstStep,
          status: newStatus,
          questions: analysisData?.questions || [],
          evaluation_criteria: analysisData?.evaluation_criteria || [],
        };
      }

      // Update second step (Initial Assessment)
      const secondStep = updatedSteps[1];
      if (secondStep && secondStep.id === 'initial_assessment') {
        let newStatus: 'pending' | 'in_progress' | 'completed' = 'pending';

        // Check if we have initial assessment data
        const hasInitialReview = analysisData?.initial_review_result;

        // GENERATE mode: backend skips initial_reviewer_agent + create_initial_score_report
        // entirely (response_subgraph.py:10446). The step is intentionally a no-op,
        // so mark it 'completed' as soon as Document Processing is — this unlocks
        // downstream tabs (Response Refinement, etc.) without forcing the user to
        // wait on a step that will never produce data. The tab itself is hidden
        // from the rendered tab list further down so the user doesn't see a
        // tab whose content is "this step doesn't apply here".
        if (isGenerateMode(analysisData) && firstStepStatus === 'completed') {
          newStatus = 'completed';
        }
        // Mark as completed if we have initial review data from LangGraph
        else if (hasInitialReview) {
          newStatus = 'completed';
        }
        // Mark as in_progress if first tab is completed, but only while the
        // overall run is still active — otherwise this step was skipped
        // (e.g., initial_reviewer_agent is skipped in GENERATE mode).
        else if (!runIsDone && firstStepStatus === 'completed') {
          newStatus = 'in_progress';
        }

        updatedSteps[1] = {
          ...secondStep,
          status: newStatus,
          initial_review: hasInitialReview,
        };
      }

      // Update third step (Response Refinement)
      const thirdStep = updatedSteps[2];
      if (thirdStep && thirdStep.id === 'response_refinement') {
        let newStatus: 'pending' | 'in_progress' | 'completed' = 'pending';

        // Check if we have refined questions data
        const hasRefinedQuestions = hasRefinedQuestionsData(analysisData?.refined_questions);
        const hasDraftedQuestions = !!(analysisData?.drafted_questions && (analysisData.drafted_questions as any[]).length > 0);
        // The Response Refinement *stage* is only complete once the quality
        // check has scored the refined work — until then, refinement is
        // mid-flight (refine_again loop, QC scoring, etc.). Without this
        // gate the step flips ✓ the moment refined_questions is populated,
        // even though QC is still running for several seconds afterwards.
        const hasQcResult = !!analysisData?.quality_check_result;
        const firstStepCompleted = updatedSteps[0]?.status === 'completed';

        // Completed only when refined/drafted output exists AND QC has
        // signed off on it. (Accept either refined or drafted as the
        // "produced output" signal — GENERATE-mode runs may have only
        // drafted_questions.)
        if ((hasRefinedQuestions || hasDraftedQuestions) && hasQcResult) {
          newStatus = 'completed';
        }
        // Run wrapped up but no QC payload — still treat as completed so
        // the stepper doesn't get stuck on a finished run.
        else if (runIsDone && (hasRefinedQuestions || hasDraftedQuestions)) {
          newStatus = 'completed';
        }
        // In-flight: refinement output exists but QC hasn't scored yet, or
        // we're loading and the previous step has finished.
        else if (!runIsDone && (hasRefinedQuestions || hasDraftedQuestions || (firstStepCompleted && isLoading))) {
          newStatus = 'in_progress';
        }
        
        updatedSteps[2] = {
          ...thirdStep,
          status: newStatus,
          refined_responses: transformRefinedQuestions(analysisData?.refined_questions),
        };
      }

      // Update fourth step (Performance Analysis)
      const fourthStep = updatedSteps[3];
      if (fourthStep && fourthStep.id === 'performance_analysis') {
        let newStatus: 'pending' | 'in_progress' | 'completed' = 'pending';
        
        // Check if we have improvement analysis data or comparison content
        const hasImprovementAnalysis = analysisData?.improvement_analysis;
        const hasComparisonContent = analysisData?.comparison_content;
        const thirdStepCompleted = updatedSteps[2]?.status === 'completed';
        
        // Mark as completed if we have either improvement analysis data or comparison content from LangGraph
        if (hasImprovementAnalysis || hasComparisonContent) {
          newStatus = 'completed';
        }
        // Only mark in_progress while the run is still active.
        else if (!runIsDone && thirdStepCompleted) {
          newStatus = 'in_progress';
        }
        
        // Create ROI analysis from available data
        // ── Performance Analysis builder — grounded reads only ────────────
        // Reads from real backend state; when grounded data isn't available
        // (e.g. compare_score_improvement hasn't run yet), returns undefined
        // so the UI surfaces a "Pending" state rather than fabricated numbers.
        const refinedQs = (analysisData?.refined_questions || []) as any[];
        const draftedQs = (analysisData?.drafted_questions || []) as any[];

        // In GENERATE mode the run still emits a refined_questions list as a
        // side-effect of the AUTO dispatch path, but those rows lack question
        // text, improvement_score, and improvement_summary — surfacing them
        // would render "Question N / —" / blank-highlights rows in the export
        // table. Restrict the summary to drafted items in GENERATE mode;
        // REFINE mode keeps the merged list (which is what it expects).
        // Defense-in-depth: also drop items with empty question text. The
        // mode-detection above (isGenerateMode) can return false on
        // rehydrated state if improvement_analysis arrives as null instead
        // of {} — the question-text filter catches the same garbage rows
        // that the mode filter would have caught, without affecting normal
        // REFINE runs (where every refined item has populated question text).
        const isUsable = (q: any) =>
          typeof q?.question === 'string' && q.question.trim().length > 0;
        const summarySources = (isGenerateMode(analysisData)
          ? draftedQs
          : [...refinedQs, ...draftedQs]
        ).filter(isUsable);

        // Real per-question scores from the rubric (improvement_score). Items
        // where scoring failed return null and are excluded from aggregates.
        // NOTE: improvement_score is a heuristic *delta-specificity* signal in
        // [0, 100] but typically lands in single digits (a refined response
        // rarely adds >20% more named-entity / numeric density). High-impact
        // and confidence are therefore re-keyed below to the OVERALL anchored
        // QC vs initial gain (improvement_analysis.score_gain / percent_gain),
        // which is the metric that actually reflects "did this AI run move
        // the needle on the bid".
        const allScored = summarySources
          .map((q) => q.improvement_score)
          .filter((s): s is number => typeof s === 'number');
        // Top quartile of typical heuristic distribution: scores >= 15 mean
        // the refinement added substantial specificity (numbers, named
        // projects, certifications) on top of the original.
        const highImpactCount = allScored.filter((s) => s >= 15).length;
        const highQualityCount = allScored.filter((s) => s >= 10).length;
        const avgPerQuestionScore = allScored.length
          ? Math.round(allScored.reduce((a, b) => a + b, 0) / allScored.length)
          : null;

        // Sections actually represented in the refined/drafted set.
        const sectionsImproved = Array.from(
          new Set(
            summarySources
              .map((q) => q.section)
              .filter((s): s is string => typeof s === 'string' && s.length > 0)
          )
        );

        const createROIFromComparison = () => {
          if (!hasImprovementAnalysis) return undefined;
          const ia: any = hasImprovementAnalysis;
          // Only produce a result when the backend has computed real numeric
          // gains. The legacy formula (baseline + Math.min(25, count*2)) is
          // gone — if compare_score_improvement hasn't run, we surface
          // "Pending" not a number.
          if (
            typeof ia.initial_score !== 'number' ||
            typeof ia.final_score !== 'number'
          ) {
            return undefined;
          }
          return {
            percentage_improvement: typeof ia.percent_gain === 'number'
              ? ia.percent_gain
              : Math.round(((ia.final_score - ia.initial_score) / Math.max(1, ia.initial_score)) * 10000) / 100,
            baseline_score: ia.initial_score,
            enhanced_score: ia.final_score,
            score_gain: typeof ia.score_gain === 'number'
              ? ia.score_gain
              : ia.final_score - ia.initial_score,
            roi_assessment: ia.roi_assessment || null,
            total_questions_improved: refinedQs.length + draftedQs.length,
            high_impact_improvements: highImpactCount,
            summary: ia.justification || ia.summary || '',
            // Confidence is derived from the OVERALL anchored uplift rather
            // than per-question heuristic averages — the per-question signal
            // is delta-specificity in [0, 100] but lands in single digits, so
            // averaging it would always read 'Low' regardless of how much the
            // bid actually improved. The thresholds below match the +2.5%
            // success gate the system uses to decide whether to ship the
            // refined version: passes-with-margin → High, passes → Medium,
            // below the gate → Low.
            confidence_level: (() => {
              const pctGain = typeof ia.percent_gain === 'number'
                ? ia.percent_gain
                : (typeof ia.final_score === 'number' && typeof ia.initial_score === 'number'
                    ? ((ia.final_score - ia.initial_score) / Math.max(1, ia.initial_score)) * 100
                    : null);
              if (pctGain === null) return 'Unknown';
              if (pctGain >= 5) return 'High';
              if (pctGain >= 2.5) return 'Medium';
              return 'Low';
            })(),
          };
        };

        const createExportSummary = () => {
          // Always safe to produce — uses only the per-question fields we
          // already trust (set by the rubric scorer in _score_single_refinement).
          //
          // For drafted rows in generate mode, the rubric scorer doesn't run
          // (no baseline to compare against), so improvement_score and
          // improvement_summary are unset. Rather than show blank columns,
          // we synthesize useful signals from KB grounding and length data.
          const stats = summarySources.map((q: any, index: number) => {
            const original = q.original_response || '';
            const refined = q.refined_response || q.drafted_response || '';
            const isDrafted = !refinedQs.includes(q);
            const wordCount = (s: string) => typeof s === 'string' ? s.split(/\s+/).filter(Boolean).length : 0;
            const originalLen = wordCount(original);
            const refinedLen = wordCount(refined);

            // Synthesize improvement_score for drafted rows: combine KB
            // grounding confidence (when available) with draft completeness.
            let improvementScore: number | null = typeof q.improvement_score === 'number' ? q.improvement_score : null;
            if (improvementScore === null && isDrafted) {
              const kbConf = q.kb_retrieval?.coverage?.confidence_score;
              if (typeof kbConf === 'number') {
                improvementScore = Math.round(Math.max(0, Math.min(1, kbConf)) * 100);
              } else if (refinedLen > 0) {
                // Fallback: completeness vs ~350 word target, capped at 100.
                improvementScore = Math.round(Math.min(1, refinedLen / 350) * 100);
              }
            }

            // Synthesize key_enhancements for drafted rows: surface what the
            // AI actually did (KB sources cited, coverage tier, length).
            let enhancements: string[] = q.improvement_summary ? [q.improvement_summary] : [];
            if (enhancements.length === 0 && isDrafted) {
              const sourceCount = q.kb_retrieval?.source_count ?? (q.sources?.length ?? 0);
              const tier = q.kb_retrieval?.coverage?.assessment;
              const synth: string[] = [];
              if (sourceCount > 0) {
                synth.push(`Authored from ${sourceCount} knowledge-base source${sourceCount === 1 ? '' : 's'}`);
              }
              if (tier && typeof tier === 'string') {
                synth.push(`KB grounding: ${tier.charAt(0).toUpperCase()}${tier.slice(1)}`);
              }
              if (refinedLen > 0) {
                synth.push(`Drafted ${refinedLen} words from a blank response`);
              }
              if (synth.length === 0) {
                synth.push('AI-authored draft (no KB context available)');
              }
              enhancements = synth;
            }

            return {
              question: (q.question || `Question ${index + 1}`).length > 80
                ? (q.question || `Question ${index + 1}`).substring(0, 80) + '…'
                : (q.question || `Question ${index + 1}`),
              original_length: originalLen,
              refined_length: refinedLen,
              improvement_score: improvementScore,
              key_enhancements: enhancements,
              section: q.section || undefined,
              source: (isDrafted ? 'drafted' : 'refined') as 'drafted' | 'refined',
            };
          });

          if (!stats.length && !hasImprovementAnalysis) return undefined;

          return {
            generated_on: new Date().toISOString(),
            total_refinements: summarySources.length,
            average_improvement:
              avgPerQuestionScore === null ? null : avgPerQuestionScore,
            high_quality_responses: highQualityCount,
            sections_improved: sectionsImproved,
            refinement_statistics: stats,
          };
        };

        updatedSteps[3] = {
          ...fourthStep,
          status: newStatus,
          roi_analysis: createROIFromComparison(),
          export_summary: createExportSummary(),
        };
      }

      // Update fifth step (Final Report)
      const fifthStep = updatedSteps[4];
      if (fifthStep && fifthStep.id === 'final_report') {
        let newStatus: 'pending' | 'in_progress' | 'completed' = 'pending';

        // Don't mark "Final Report" complete just because `final_report`
        // landed — the response subgraph fires generate_final_report BEFORE
        // the final_approval HITL pause, so without the second gate the
        // step shows ✓ while Performance Analysis (which runs AFTER
        // approval) is still pending. Reading order on the stepper would
        // be: 4 in_progress, 5 ✓ — confusing.
        //
        // Require BOTH:
        //  • analysisData.final_report (generate_final_report ran)
        //  • analysisData.comparison_content (export_response_comparison
        //    ran after the user approved → end of pipeline)
        // so the ✓ only appears once the run has truly finished.
        const hasFinalReport = analysisData?.final_report;
        const hasComparisonContent = (analysisData as any)?.comparison_content;
        const fourthStepCompleted = updatedSteps[3]?.status === 'completed';

        if (hasFinalReport && hasComparisonContent) {
          newStatus = 'completed';
        }
        // Either we're still streaming, or we have the report but the
        // post-approval export hasn't fired yet (HITL pause / awaiting
        // approval) — either way, surface in_progress not completed.
        else if (
          (!runIsDone && fourthStepCompleted) ||
          (hasFinalReport && !hasComparisonContent)
        ) {
          newStatus = 'in_progress';
        }
        
        // Phase D — prefer the backend-emitted FinalReportPayload (when the
        // run completed via the new pipeline). Fall back to the client-side
        // synthesizer for older runs that pre-date Phase D so the tab still
        // renders fully without a backfill migration.
        const backendPayload = (analysisData as any)?.final_report_payload;
        const synthesised = hasFinalReport
          ? buildFinalReport({
              refinedQuestions: transformRefinedQuestions(analysisData?.refined_questions),
              draftedQuestions: (analysisData as any)?.drafted_questions || [],
              questions: analysisData?.questions || [],
              evaluationCriteria: analysisData?.evaluation_criteria || [],
              complianceItems: (analysisData as any)?.compliance_items || [],
              pastTenderMatches: (analysisData as any)?.past_tender_matches || {},
              selectionMetadata: (analysisData as any)?.question_selection_metadata,
              legacyMarkdown: typeof hasFinalReport === 'string' ? hasFinalReport : '',
              // Anchored 0-100 quality score, in priority order:
              //   1. quality_check_result.score — anchored rubric output
              //      from the new QC node (post-fix); on the same scale
              //      as initial_review.score.
              //   2. improvement_analysis.final_score — post-approval
              //      comparison output (may differ from QC if the
              //      compare node renormalises).
              //   3. initial_review.score — pre-refinement baseline as a
              //      last resort so the circle shows SOMETHING grounded.
              fallbackOverallScore:
                typeof (analysisData as any)?.quality_check_result?.score === 'number'
                  ? (analysisData as any).quality_check_result.score
                  : typeof (analysisData as any)?.improvement_analysis?.final_score === 'number'
                    ? (analysisData as any).improvement_analysis.final_score
                    : typeof analysisData?.initial_review_result?.score === 'number'
                      ? analysisData.initial_review_result.score
                      : null,
              submissionJustification:
                (analysisData as any)?.quality_check_result?.justification ||
                '',
              modelId:
                (analysisData as any)?.ai_model_id ||
                (analysisData as any)?.model_id ||
                'gpt-5.5-2026-04-23',
              responseMode: isGenerateMode(analysisData) ? 'generate' : undefined,
            })
          : undefined;
        // Pre-fix runs persisted `final_report_payload.overall_score` as
        // the heuristic specificity average (e.g. 13.5/100) and emitted
        // `evidence_summary.positive_signal = null`. Override both with
        // the synthesised values when available so the headline score and
        // grounding signal reflect the anchored QC + retrieval data even
        // for runs that completed before the backend fix shipped.
        let finalReportData: any =
          backendPayload && typeof backendPayload === 'object'
            ? backendPayload
            : synthesised;

        if (
          backendPayload &&
          typeof backendPayload === 'object' &&
          synthesised &&
          typeof synthesised === 'object'
        ) {
          const merged: any = { ...(backendPayload as any) };

          // Score — prefer anchored QC; fall back to whatever buildFinalReport
          // chose (which already runs the same priority order).
          //
          // In generate mode the backend often persists overall_score=0
          // because the rubric scorer never ran. If our synthesised score
          // (KB-grounding average) is also null, we still don't want to
          // surface 0/100 — that reads as "you scored zero" instead of
          // "no scoring applies." Force null in that case so the UI shows
          // "Score pending" instead.
          const anchored = (synthesised as any).overall_score;
          if (typeof anchored === 'number' && anchored > 0) {
            merged.overall_score = anchored;
          } else if (
            isGenerateMode(analysisData) &&
            typeof merged.overall_score === 'number' &&
            merged.overall_score === 0
          ) {
            merged.overall_score = null;
          }

          // Evidence summary — backfill positive_signal when the backend
          // payload predates the field. Keep the backend's tier_counts /
          // flagged_question_ids since those are computed correctly there.
          const beEv = (backendPayload as any)?.evidence_summary;
          const synEv = (synthesised as any)?.evidence_summary;
          if (synEv?.positive_signal) {
            merged.evidence_summary = {
              ...(beEv || {}),
              positive_signal: beEv?.positive_signal ?? synEv.positive_signal,
            };
          }

          finalReportData = merged;
        }

        updatedSteps[4] = {
          ...fifthStep,
          status: newStatus,
          final_report: finalReportData,
        };
      }
      
      return updatedSteps;
    });
  }, [analysisData, isLoading, isCompletedAnalysis, progress?.isComplete]);

  // Removed auto-tab switching - let users navigate manually
  // useEffect(() => {
  //   // Auto-advance to the next tab when a step is completed or in progress
  //   const activeStepIndex = displaySteps.findIndex(step => step.status === 'in_progress');
  //   if (activeStepIndex !== -1 && activeStepIndex !== activeTabIndex) {
  //     setActiveTabIndex(activeStepIndex);
  //   }
  // }, [displaySteps, activeTabIndex]);

  const handleTabClick = (index: number) => {
    // Only allow clicking on completed steps or the current/next step
    const step = displaySteps[index];
    const canClick = step.status === 'completed' || step.status === 'in_progress' || step.status === 'error' ||
                    (index === 0) || // Always allow first step
                    (index <= activeTabIndex + 1); // Allow next step
    
    if (canClick) {
      setActiveTabIndex(index);
    }
  };

  const currentStep = displaySteps[activeTabIndex];

  // GENERATE mode hides the Initial Assessment tab — the backend skips that
  // node entirely (response_subgraph.py:10446) so showing a tab that will
  // never have content is just noise. We keep `displaySteps` intact (the
  // index-based status updates depend on stable positions) and render a
  // filtered subset for the tab strip only. `activeTabIndex` continues to
  // point at the original `displaySteps` index, so the existing
  // `activeTabIndex === 2/3/4` content branches keep working unchanged.
  const isGenMode = isGenerateMode(analysisData);
  const visibleSteps = isGenMode
    ? displaySteps.filter((s) => s.id !== 'initial_assessment')
    : displaySteps;

  // If the user happens to be on the Initial Assessment tab (index 1) when
  // mode detection resolves to GENERATE — e.g., they navigated there before
  // the response_mode field streamed in — snap forward to Response Refinement
  // (the next meaningful tab) so they don't end up stranded with no tab
  // highlighted in the strip.
  useEffect(() => {
    if (isGenMode && activeTabIndex === 1) {
      setActiveTabIndex(2);
    }
  }, [isGenMode, activeTabIndex]);

  // GENERATE-mode label swaps. The pipeline still runs the same nodes, but
  // their user-facing framing changes when the run authored every answer
  // from scratch (no inline content to "refine"). Centralised here so the
  // tab strip, the step card heading, and the Pending empty state stay in
  // sync — instead of three independent ternaries scattered through render.
  const stepDisplayName = (step: AgentFlowStep): string => {
    if (isGenMode && step.id === 'response_refinement') return 'Response Generation';
    return step.name;
  };
  const stepDisplayDescription = (step: AgentFlowStep): string => {
    if (isGenMode && step.id === 'response_refinement') {
      return 'Drafting each response from your knowledge base and scoring it against the tender\'s evaluation criteria.';
    }
    return step.description;
  };

  const getStepIcon = (step: AgentFlowStep, size: number = 24) => {
    if (step.status === 'in_progress') {
      return <Loader2 size={size} className="spin" />;
    }

    switch (step.id) {
      case 'process_pdf':
        return <FileText size={size} />;
      case 'analyze_structure':
        return <Search size={size} />;
      case 'extract_patterns':
        return <Brain size={size} />;
      case 'generate_responses':
        return <PenTool size={size} />;
      case 'quality_check':
        return <CheckCircle size={size} />;
      default:
        return <FileText size={size} />;
    }
  };

  const getStatusIcon = (status: 'pending' | 'in_progress' | 'completed' | 'error') => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} />;
      case 'error':
        return <XCircle size={16} />;
      case 'in_progress':
        return <Loader2 size={16} className="spin" />;
      default:
        return <Clock size={16} />;
    }
  };

  const getStatusText = (status: 'pending' | 'in_progress' | 'completed' | 'error') => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'error':
        return 'Error';
      case 'in_progress':
        return 'In Progress';
      default:
        return 'Pending';
    }
  };

  const calculateProgress = () => {
    // Use streaming progress if available
    if (progress && progress.progress > 0) {
      return progress.progress;
    }
    
    // Fallback to step-based progress
    const completedSteps = displaySteps.filter(step => step.status === 'completed').length;
    return (completedSteps / displaySteps.length) * 100;
  };

  const isTabDisabled = (index: number, step: AgentFlowStep) => {
    // First tab is always enabled
    if (index === 0) return false;
    
    // Allow clicking on completed steps
    if (step.status === 'completed' || step.status === 'error') return false;
    
    // Allow clicking on the current step
    if (step.status === 'in_progress') return false;
    
    // Allow clicking one step ahead if previous steps are completed
    const previousStep = displaySteps[index - 1];
    return !previousStep || (previousStep.status !== 'completed' && previousStep.status !== 'error');
  };

  const handleHumanRefine = async (
    questionId: string, 
    prompt: string, 
    callbacks: {
      onChunk: (chunk: string) => void;
      onComplete: (finalResponse: string) => void;
      onError: (error: Error) => void;
    }
  ) => {
    // Find the current step and the specific response to refine
    const refinementStep = displaySteps.find(step => step.id === 'response_refinement');
    const responseToRefine = refinementStep?.refined_responses?.find(r => r.question_id === questionId);
    
    if (!responseToRefine) {
      callbacks.onError(new Error('Response not found for refinement'));
      return;
    }

    try {
      // Create enhanced callbacks - don't update state here, wait for user acceptance
      const enhancedCallbacks = {
        onChunk: callbacks.onChunk,
        onComplete: (finalResponse: string) => {
          // Don't update state here - wait for user to accept/reject
          callbacks.onComplete(finalResponse);
        },
        onError: callbacks.onError
      };

      await refineResponse(
        responseToRefine.question,
        responseToRefine.refined_response,
        prompt,
        enhancedCallbacks
      );
    } catch (error) {
      console.error('Refinement failed:', error);
      callbacks.onError(error as Error);
    }
  };

  const handleAcceptRefinement = async (questionId: string, refinedText: string) => {
    // Check if threadId is available for persistence
    if (!threadId) {
      alert('Cannot save refinement: No active analysis session found. Please start a new analysis to save refinements.');
      return;
    }

    // Update the display steps state with the accepted refined response
    const updatedSteps = displaySteps.map(step => {
      if (step.id === 'response_refinement' && step.refined_responses) {
        return {
          ...step,
          refined_responses: step.refined_responses.map(response => {
            if (response.question_id === questionId) {
              return {
                ...response,
                refined_response: refinedText
              };
            }
            return response;
          })
        };
      }
      return step;
    });

    setDisplaySteps(updatedSteps);

    // Save to database if threadId is available
    if (threadId) {
      try {
        // Get the updated refined questions/responses for persistence
        const updatedRefinementStep = updatedSteps.find(step => step.id === 'response_refinement');
        if (updatedRefinementStep?.refined_responses) {
          const refinedQuestions = updatedRefinementStep.refined_responses.map(response => ({
            question_id: response.question_id,
            question: response.question,
            original_response: response.original_response,
            refined_response: response.refined_response,
            improvement_score: response.improvement_score,
            improvement_summary: response.improvement_summary,
            refinement_reason: response.refinement_reason
          }));

          const saveResult = await AnalysisAPI.updateRefinedQuestions(threadId, refinedQuestions);
          if (!saveResult.success) {
            console.warn('Failed to save refined questions to database:', saveResult.error);
          } else {
            console.log('Refined questions saved to database successfully');
          }
        }
      } catch (saveError) {
        console.error('Error saving refined questions:', saveError);
      }
    } else {
      console.warn('No threadId available, refined response will not be persisted');
    }
  };

  const analysisStatus = getAnalysisStatus(isLoading, progress, analysisData, isCompletedAnalysis);

  return (
    <FlowContainer>
      {(isLoading || progress || analysisData) && (
        <AnalysisStatusCard 
          status={analysisStatus}
          progress={progress?.progress}
          currentStep={progress?.currentStep}
          customMessage={progress?.hasError ? progress.error : undefined}
        />
      )}
      
      <ProgressBar>
        <ProgressFill $progress={calculateProgress()} />
      </ProgressBar>

      <TabsContainer>
        <TabsList>
          {visibleSteps.map((step, visibleIndex) => {
            // Translate visible position back to the canonical displaySteps
            // index — that's the value activeTabIndex carries and the value
            // the existing `activeTabIndex === N` content branches expect.
            const originalIndex = displaySteps.indexOf(step);
            const isActive = originalIndex === activeTabIndex;
            const isCompleted = step.status === 'completed';
            const isDisabled = isTabDisabled(originalIndex, step);

            return (
              <TabItem
                key={step.id}
                $active={isActive}
                $completed={isCompleted}
                $disabled={isDisabled}
                onClick={() => handleTabClick(originalIndex)}
                disabled={isDisabled}
              >
                <TabIcon $status={step.status}>
                  {step.status === 'completed' ? (
                    <CheckCircle size={16} />
                  ) : step.status === 'in_progress' ? (
                    <Loader2 size={16} className="spin" />
                  ) : step.status === 'error' ? (
                    <XCircle size={16} />
                  ) : (
                    visibleIndex + 1
                  )}
                </TabIcon>
                {stepDisplayName(step)}
              </TabItem>
            );
          })}
        </TabsList>
      </TabsContainer>

      <StepsContainer>
        {currentStep && (
          <TabContent>
            {/* Only show step card for first tab if we're loading or have data */}
            {(activeTabIndex !== 0 || isLoading || analysisData) && (
              <StepCard $status={currentStep.status}>
                <StepHeader>
                  <StepIcon $status={currentStep.status}>
                    {getStepIcon(currentStep)}
                  </StepIcon>
                  <StepContent>
                    <StepTitle>{stepDisplayName(currentStep)}</StepTitle>
                    <StepDescription>{stepDisplayDescription(currentStep)}</StepDescription>
                    <StepStatus $status={currentStep.status}>
                      {getStatusIcon(currentStep.status)}
                      {getStatusText(currentStep.status)}
                      {currentStep.timestamp && ` • ${currentStep.timestamp.toLocaleTimeString()}`}
                    </StepStatus>
                  </StepContent>
                </StepHeader>

                {currentStep.error && (
                  <ErrorMessage>
                    {currentStep.error}
                  </ErrorMessage>
                )}
              </StepCard>
            )}

            {/* Special handling for Document Processing tab (first tab) */}
            {activeTabIndex === 0 && (
              <>
                {/* Show no analysis state when not loading and no data */}
                {!isLoading && !analysisData && (
                  <NoAnalysisState>
                    <FileText size={48} />
                    <NoAnalysisText>No ongoing response generation</NoAnalysisText>
                    <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm }}>
                      Start a new tender response to see results here
                    </p>
                  </NoAnalysisState>
                )}

                {/* Show DocumentProcessingResults when loading OR when we have data */}
                {(isLoading || analysisData) && (
                  <DocumentProcessingResults
                    questions={analysisData?.questions || []}
                    evaluationCriteria={analysisData?.evaluation_criteria || []}
                    requirementsSummary={analysisData?.requirements_summary}
                    responses={{}}
                    isStreaming={isCompletedAnalysis ? false : isLoading}
                    streamingProgress={{
                      questionsCount: analysisData?.questions?.length || 0,
                      criteriaCount: analysisData?.evaluation_criteria?.length || 0,
                      currentAction: progress?.message || 'Processing document...'
                    }}
                    // LangExtract provenance fields
                    extractionVisualizationHtml={analysisData?.extraction_visualization_html}
                    extractionMethod={analysisData?.extraction_method_used}
                    detectedTenderType={analysisData?.detected_tender_type}
                  />
                )}
              </>
            )}

            {/* Special handling for Initial Assessment tab (second tab) */}
            {activeTabIndex === 1 && (
              <>
                {/* Show loading state when no data and not processing */}
                {!isLoading && !analysisData?.initial_review_result && currentStep.status === 'pending' && (
                  <NoAnalysisState>
                    <FileText size={48} />
                    <NoAnalysisText>Initial Assessment Pending</NoAnalysisText>
                    <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm }}>
                      Assessment will begin after document processing is complete
                    </p>
                  </NoAnalysisState>
                )}

                {/* Show InitialAssessmentResults when loading OR when we have data.
                    scoreReport comes from the backend's create_initial_score_report
                    node (structured; grounded in the reviewer's criteria scores). */}
                {(isLoading && currentStep.status !== 'pending') || analysisData?.initial_review_result ? (
                  <InitialAssessmentResults
                    initialReview={analysisData?.initial_review_result}
                    scoreReport={analysisData?.initial_score_report}
                    isProcessing={isCompletedAnalysis ? false : currentStep.status === 'in_progress'}
                  />
                ) : null}
              </>
            )}

            {/* Special handling for Response Refinement tab (third tab) */}
            {activeTabIndex === 2 && (
              <>
                {/* Show loading state when no data and not processing */}
                {!isLoading && !hasRefinedQuestionsData(analysisData?.refined_questions) && currentStep.status === 'pending' && (
                  <NoAnalysisState>
                    <FileText size={48} />
                    <NoAnalysisText>
                      {isGenMode ? 'Response Generation Pending' : 'Response Refinement Pending'}
                    </NoAnalysisText>
                    <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm }}>
                      {isGenMode
                        ? 'Draft responses will begin generating after document processing is complete'
                        : 'Response refinement will begin after initial assessment is complete'}
                    </p>
                  </NoAnalysisState>
                )}

                {/* P0 — Render the full Question Matrix: every extracted question with
                    its status (refined / drafted / needs draft / excluded), grounded
                    score, criterion mapping, and explainability drawer on click.
                    Replaces the previous ResponseRefinementResults linear list which
                    silently dropped the questions without inline bidder responses. */}
                {(isLoading && currentStep.status !== 'pending') || hasRefinedQuestionsData(analysisData?.refined_questions) || (analysisData?.questions && analysisData.questions.length > 0) ? (
                  <QuestionMatrix
                    questions={analysisData?.questions as any}
                    refinedResponses={currentStep.refined_responses || transformRefinedQuestions(analysisData?.refined_questions)}
                    draftedQuestions={analysisData?.drafted_questions as any}
                    evaluationCriteria={analysisData?.evaluation_criteria as any}
                    selectionMetadata={analysisData?.question_selection_metadata as any}
                    complianceItems={analysisData?.compliance_items as any}
                    pastTenderMatches={analysisData?.past_tender_matches as any}
                    refinementPathUsed={analysisData?.refinement_path_used as any}
                    threadId={(analysisData as any)?.thread_id}
                    workflowWarnings={(analysisData as any)?.workflow_warnings}
                    modelId="gpt-5.5-2026-04-23"
                    isGenerateMode={isGenMode}
                    onHumanRefine={handleHumanRefine}
                    onAcceptRefinement={handleAcceptRefinement}
                  />
                ) : null}
              </>
            )}

            {/* Special handling for Performance Analysis tab (fourth tab) */}
            {activeTabIndex === 3 && (
              <>
                {/* Show loading state when no data and not processing */}
                {!isLoading && !currentStep.roi_analysis && !currentStep.export_summary && currentStep.status === 'pending' && (
                  <NoAnalysisState>
                    <FileText size={48} />
                    <NoAnalysisText>Performance Analysis Pending</NoAnalysisText>
                    <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm }}>
                      Performance analysis will begin after response refinement is complete
                    </p>
                  </NoAnalysisState>
                )}

                {/* Show PerformanceAnalysisResults when loading OR when we have data */}
                {(currentStep.status === 'in_progress' || currentStep.roi_analysis || currentStep.export_summary) && (
                  <PerformanceAnalysisResults
                    roiAnalysis={currentStep.roi_analysis}
                    exportSummary={currentStep.export_summary}
                    isProcessing={isCompletedAnalysis ? false : currentStep.status === 'in_progress'}
                    isGenerateMode={isGenerateMode(analysisData)}
                  />
                )}
              </>
            )}

            {/* Special handling for Final Report tab (fifth tab) */}
            {activeTabIndex === 4 && (
              <>
                {/* Show loading state when no data and not processing */}
                {!isLoading && !currentStep.final_report && currentStep.status === 'pending' && (
                  <NoAnalysisState>
                    <FileText size={48} />
                    <NoAnalysisText>Final Report Pending</NoAnalysisText>
                    <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm }}>
                      Final report generation will begin after performance analysis is complete
                    </p>
                  </NoAnalysisState>
                )}

                {/* Show FinalReportResults when loading OR when we have data */}
                {(currentStep.status === 'in_progress' || currentStep.final_report) && (
                  <FinalReportResults
                    finalReport={currentStep.final_report}
                    isProcessing={isCompletedAnalysis ? false : currentStep.status === 'in_progress'}
                    isGenerateMode={isGenerateMode(analysisData)}
                  />
                )}
              </>
            )}

            {/* Show generic result for other tabs or when no specific data is available */}
            {(activeTabIndex > 4 || 
              (activeTabIndex === 1 && !currentStep.initial_review && !currentStep.score_report) ||
              (activeTabIndex === 2 && !currentStep.refined_responses) ||
              (activeTabIndex === 3 && !currentStep.roi_analysis && !currentStep.export_summary) ||
              (activeTabIndex === 4 && !currentStep.final_report)
            ) && currentStep.result && (
              <StepResult>
                {(() => {
                  const resultText = typeof currentStep.result === 'string' 
                    ? currentStep.result 
                    : JSON.stringify(currentStep.result, null, 2);
                  
                  // Check if the result contains markdown formatting
                  const hasMarkdown = /(\*\*.*?\*\*|###\s|##\s|#\s|`.*?`|\*.*?\*)/.test(resultText);
                  
                  if (hasMarkdown) {
                    // Parse markdown formatting
                    const parseMarkdown = (text: string) => {
                      return text
                        // Convert headings
                        .replace(/^### (.+)$/gm, '<h3>$1</h3>')
                        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
                        .replace(/^# (.+)$/gm, '<h1>$1</h1>')
                        // Convert bold text
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        // Convert italic text
                        .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
                        // Convert inline code
                        .replace(/`([^`]+?)`/g, '<code>$1</code>')
                        // Convert line breaks to paragraphs
                        .split('\n\n')
                        .map(paragraph => {
                          const trimmed = paragraph.trim();
                          if (!trimmed) return '';
                          if (trimmed.startsWith('<h1>') || trimmed.startsWith('<h2>') || trimmed.startsWith('<h3>')) {
                            return trimmed;
                          }
                          return `<p>${trimmed.replace(/\n/g, '<br>')}</p>`;
                        })
                        .join('');
                    };
                    
                    return (
                      <div 
                        style={{ 
                          lineHeight: '1.6',
                          fontSize: theme.typography.fontSize.sm,
                          color: theme.colors.textPrimary
                        }}
                        dangerouslySetInnerHTML={{ __html: parseMarkdown(resultText) }}
                      />
                    );
                  } else {
                    // Display as plain text for non-markdown content
                    return (
                      <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                        {resultText}
                      </pre>
                    );
                  }
                })()}
              </StepResult>
            )}
          </TabContent>
        )}
      </StepsContainer>
    </FlowContainer>
  );
};

export default AgentFlow;